import { Link } from 'react-router-dom';
import {
  Magnetic,
  Marquee as KineticMarquee,
  Parallax,
  RevealGroup,
  RevealItem,
  RevealLine,
  ScrollProgress,
} from '@/components/motion/Kinetic';
import { usePlatform } from '@/providers/PlatformProvider';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { useReveal, revealClasses } from '@/hooks/useReveal';
import { ButtonLink } from '@/components/ui/Button';
import { BrandArtwork } from '@/components/brand/BrandArtwork';
import { Hero3D } from '@/components/three/Hero3D';
import { SectionHeading } from '@/components/home/SectionHeading';
import { OccasionStrip } from '@/components/home/OccasionStrip';
import { ProductShowcase } from '@/components/home/ProductShowcase';

/**
 * Homepage — BUILD-PLAN §5, §11, §57.
 *
 * Editorial rather than template-shaped: an asymmetric split hero, extreme
 * scale contrast between the display serif and the letter-spaced eyebrows,
 * full-bleed tonal bands, and generous vertical rhythm. §5 explicitly rejects
 * "generic Shopify-style templates" and "excessive rounded cards".
 *
 * Every section is backed by real API data or genuine brand copy from
 * platform configuration. Sections whose data is empty remove themselves
 * rather than showing placeholder merchandising (§86).
 *
 * Still outstanding for a complete §11 homepage, recorded in COMPLIANCE.md:
 * editorial photography, Glanz Luxe Moments (needs reviews), the social feed
 * (needs an approved API) and the newsletter form (needs the marketing
 * module). None are faked here.
 */
export function HomePage() {
  const { config, isVisible } = usePlatform();
  const { brand } = config;

  usePageMetadata({
    title: 'Luxury Gifting & Personalised Packaging',
    description: brand.description,
  });

  return (
    <>
      <ScrollProgress />

      <Hero brandName={brand.name} tagline={brand.tagline} description={brand.description} />

      <Marquee />

      {/*
        Rhythm, tightened.
        --------------------------------------------------------------------
        gap-40 between sections and py-32 around them measured generous in
        isolation and read as emptiness in sequence — the page ran past five
        thousand pixels with only five sections in it. Space should separate
        ideas, not stand in for them.
      */}
      <div className="mx-auto flex max-w-7xl flex-col gap-16 px-5 py-16 sm:px-6 lg:gap-24 lg:px-8 lg:py-20">
        {isVisible('store') && (
          <Reveal>
            <section>
              <SectionHeading
                index={1}
                eyebrow="Find the moment"
                title="Shop by occasion"
                description="Every celebration deserves something considered."
                link={{ to: '/occasions', label: 'All occasions' }}
              />
              <div className="mt-10">
                <OccasionStrip />
              </div>
            </section>
          </Reveal>
        )}

        {isVisible('store') && (
          <Reveal>
            <ProductShowcase
              index={2}
              eyebrow="Curated"
              title="Featured pieces"
              description="A selection chosen for the season."
              filters={{ featured: true, sort: 'newest' }}
              link={{ to: '/shop?featured=1', label: 'View all' }}
            />
          </Reveal>
        )}

        {isVisible('personalisation') && (
          <Reveal>
            <PersonalisationFeature />
          </Reveal>
        )}

        {isVisible('store') && (
          <Reveal>
            <ProductShowcase
              index={4}
              eyebrow="Loved by our customers"
              title="Best sellers"
              filters={{ bestsellers: true, sort: 'newest' }}
              link={{ to: '/shop?bestsellers=1', label: 'View all' }}
            />
          </Reveal>
        )}

        <Reveal>
          <Craft />
        </Reveal>
      </div>

      <BrandStory tagline={brand.tagline} />
    </>
  );
}

/* ------------------------------------------------------------------------ */
/* Hero — §57                                                                */
/* ------------------------------------------------------------------------ */

function Hero({
  brandName,
  tagline,
  description,
}: {
  brandName: string;
  tagline: string;
  description: string;
}) {
  return (
    <section className="relative overflow-hidden border-b border-line bg-surface-muted/40">
      {/*
        A 12-column grid used ASYMMETRICALLY, and that is the whole point.
        ------------------------------------------------------------------
        The previous hero was a 6/6 split with the type centred in its half —
        balanced, legible and utterly forgettable. Balance is what makes a
        layout read as a template.

        Type now runs 1–6 and the artwork 6–13, so they OVERLAP by a column.
        The tension of one element crossing into another's territory is what
        makes a composition look art-directed rather than assembled, and it
        costs nothing in legibility because the overlap is behind the frame.
      */}
      <div className="mx-auto grid max-w-7xl grid-cols-12 items-center gap-y-12 px-5 py-16 sm:px-6 lg:px-8 lg:py-20">
        {/* ------------------------------------------------------ type */}
        <div className="relative z-10 col-span-12 md:col-span-7 md:row-start-1 lg:col-span-6 lg:col-start-1 lg:pr-4">
          {/*
            A rule and an index, borrowed from print. Two marks, almost no
            ink, and the page immediately reads as edited rather than
            generated.
          */}
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-accent/60" aria-hidden="true" />
            <RevealLine className="eyebrow !mt-0">{brandName}</RevealLine>
          </div>

          {/*
            Restrained on purpose: 5xl, not 7xl.
            ----------------------------------------------------------------
            Oversized display type is the cheapest way to look designed and
            the client explicitly did not want it. The drama comes from the
            composition — the overlap, the offset second line, the negative
            space — so the type can afford to behave.
          */}
          <h1 className="mt-7 font-display text-[2rem] leading-[1.08] tracking-[-0.015em] text-ink min-[420px]:text-[2.5rem] sm:text-5xl">
            <RevealLine delay={0.08}>Made with Purpose.</RevealLine>

            {/* Indented, so the two lines step rather than stack. */}
            <RevealLine delay={0.18} className="block italic text-accent lg:pl-14">
              Packaged with Love.
            </RevealLine>
          </h1>

          <RevealGroup className="mt-10" stagger={0.1}>
            <RevealItem>
              {/* Hung off a rule rather than sitting on the margin — the
                  detail that separates a page from a document. */}
              <p className="max-w-sm border-l border-line pl-5 text-[0.9375rem] leading-[1.9] text-muted">
                {description}
              </p>
            </RevealItem>

            <RevealItem className="mt-10">
              <div className="flex flex-col gap-3 [&_a]:w-full [&>*]:w-full sm:flex-row sm:[&_a]:w-auto sm:[&>*]:w-auto">
                <Magnetic strength={0.2}>
                  <ButtonLink to="/shop" size="lg">
                    Shop the Collection
                  </ButtonLink>
                </Magnetic>
                <Magnetic strength={0.2}>
                  <ButtonLink to="/bespoke-gifting" variant="secondary" size="lg">
                    Create a Bespoke Gift
                  </ButtonLink>
                </Magnetic>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>

        {/* --------------------------------------------------- artwork */}
        {/*
          Starts at column 6, one BEFORE the type column ends, so the frame
          crosses behind the headline on wide screens. Full width below lg,
          where an overlap would only crowd.
        */}
        {/* row-start-1 on BOTH children is what makes them share a row and
            overlap. Without it the grid sees columns 1-6 and 6-12 as 13
            columns of content and wraps the artwork onto a new row — which
            is exactly what it did. */}
        <div className="relative col-span-12 md:col-span-5 md:row-start-1 lg:col-span-7 lg:col-start-6">
          <div className="relative mx-auto aspect-4/5 w-full max-w-xs shadow-raised sm:max-w-sm md:mx-0 md:ml-auto lg:max-w-md">
            <Hero3D seed={tagline} className="h-full w-full" />

            {/*
              A thin inset rule INSIDE the frame — the way a mounted print
              sits inside its mat. Decorative, non-interactive, and one of
              the cheapest ways to make an image read as considered.
            */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-4 border border-ivory/15"
            />
          </div>

          {/*
            Two drifting panels rather than one, at different depths and
            speeds. A single floating card reads as an accident; two reads as
            a decision, and the differing parallax is what creates the sense
            of layered space.
          */}
          <Parallax
            distance={44}
            className="absolute -bottom-12 -left-10 hidden aspect-square w-44 shadow-card lg:block xl:w-52"
          >
            <BrandArtwork seed={`${tagline}-detail`} motif="ribbon" ratio="square" />
          </Parallax>

          <Parallax
            distance={-26}
            className="absolute -right-4 -top-10 hidden aspect-3/4 w-28 shadow-card xl:block"
          >
            <BrandArtwork seed={`${tagline}-accent`} motif="monogram" ratio="portrait" />
          </Parallax>

          {/* Caption set against the frame's edge, as a plate number would
              be under a printed image. */}
          <p className="mt-7 hidden items-center justify-end gap-3 text-[0.6875rem] uppercase tracking-[0.25em] text-muted lg:flex">
            <span className="h-px w-8 bg-line" aria-hidden="true" />
            The Signature Collection
          </p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------ */
/* Marquee — a quiet band of brand values                                    */
/* ------------------------------------------------------------------------ */

const MARQUEE_ITEMS = [
  'Hand-finished packaging',
  'Personalised with intention',
  'Considered gifting since day one',
  'Delivered beautifully',
];

function Marquee() {
  return (
    <div className="border-b border-line bg-ink py-4 text-ivory/80">
      {/* Genuinely continuous: the track holds two copies and translates by
          exactly -50%, so the loop has no seam. Falls back to a static,
          centred strip under prefers-reduced-motion — see Kinetic.tsx. */}
      <KineticMarquee items={MARQUEE_ITEMS} speed={38} />
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Personalisation feature — §10/§15                                          */
/* ------------------------------------------------------------------------ */

/*
 * Two columns from `md`, not `lg`.
 * ---------------------------------------------------------------------------
 * This is the fault that made every screen below a laptop look unfinished.
 * Splitting only at `lg` meant the whole tablet band got ONE column: the
 * artwork full width, then the heading and paragraph set left underneath it,
 * with the right half of an 834px viewport left empty. Content all down one
 * side with dead space beside it is not a stacked layout, it is a broken one
 * — and the same mistake was repeated in every product grid.
 */
function PersonalisationFeature() {
  return (
    <section className="grid items-center gap-8 md:grid-cols-2 md:gap-10 lg:gap-16">
      <div className="relative order-2 mx-auto aspect-4/5 w-full max-w-sm shadow-card md:order-1 md:max-w-none lg:aspect-square">
        <BrandArtwork seed="personalisation-feature" motif="monogram" ratio="portrait" />
      </div>

      <div className="order-1 md:order-2">
        {/* Section 03 in the numbered run — this one is not a
            SectionHeading because its lockup sits inside a split layout. */}
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className="font-display text-sm text-accent/70">
            03
          </span>
          <span className="h-px w-8 bg-accent/50" aria-hidden="true" />
          <p className="eyebrow !mt-0">Made for them</p>
        </div>

        <h2 className="mt-5 font-display text-[1.75rem] leading-[1.15] text-ink sm:text-4xl lg:text-[2.75rem]">
          A name, an initial,
          <br />
          <span className="italic text-accent">a date that mattered.</span>
        </h2>

        <p className="mt-6 max-w-md text-[0.9375rem] leading-[1.85] text-muted">
          Personalisation is where a gift stops being an object and starts being a memory.
          Engraved, monogrammed or printed — applied by hand, with care.
        </p>

        <div className="mt-9">
          <Link
            to="/shop?personalisable=1"
            className="group inline-flex items-center gap-3 border-b border-accent pb-1.5 text-sm tracking-wide text-ink transition-colors hover:text-accent"
          >
            Explore personalised gifts
            <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">
              →
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------ */
/* Craft — numbered editorial list                                            */
/* ------------------------------------------------------------------------ */

const CRAFT = [
  {
    title: 'Thoughtfully curated',
    body: 'Each piece is chosen for how it feels to receive, not only to give.',
  },
  {
    title: 'Personalised with intention',
    body: 'Names, initials and messages, applied by hand with genuine care.',
  },
  {
    title: 'Packaged with love',
    body: 'Presentation designed to make the unboxing part of the gift itself.',
  },
];

function Craft() {
  return (
    <section>
      <SectionHeading index={5} eyebrow="Why Glanz Luxe" title="The difference is in the detail" />

      {/*
        Ruled rows, not three floating columns.
        --------------------------------------------------------------------
        The previous version gave each point its own top border and an equal
        share of the width — the arrangement a table would choose. Running
        them as rows divided by a single hairline, with the numeral held in
        its own narrow column, is how an editorial page sets a list: the eye
        gets a spine to travel down instead of three separate starts.
      */}
      <ol className="mt-12 border-t border-line">
        {CRAFT.map((item, index) => (
          <li
            key={item.title}
            className="group grid grid-cols-12 items-baseline gap-x-6 gap-y-2 border-b border-line py-7 transition-colors hover:bg-surface-muted/40 sm:py-8"
          >
            <span
              aria-hidden="true"
              className="col-span-2 font-display text-xl text-accent/70 transition-colors group-hover:text-accent sm:col-span-1 sm:text-2xl"
            >
              {String(index + 1).padStart(2, '0')}
            </span>

            <h3 className="col-span-10 font-display text-xl text-ink sm:col-span-4 sm:text-2xl">
              {item.title}
            </h3>

            {/* Offset into the last columns so the measure stays readable and
                the row keeps an open middle rather than filling edge to edge. */}
            <p className="col-span-10 col-start-3 text-sm leading-[1.8] text-muted sm:col-span-6 sm:col-start-7">
              {item.body}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* Brand story — §58, full-bleed dark band for contrast                       */
/* ------------------------------------------------------------------------ */

function BrandStory({ tagline }: { tagline: string }) {
  const { ref, revealed } = useReveal<HTMLDivElement>();

  return (
    /*
      The dark band — §58.
      ----------------------------------------------------------------------
      What stood here was dark, and that was the whole of its ambition: a
      centred column at max-w-3xl with every line stacked down the middle. A
      colour inversion is not a composition, and centred text is the safest
      arrangement there is, so the section read as plain however strong the
      contrast behind it.

      Rebuilt as a cinematic band: the image runs to the true left edge of the
      viewport rather than stopping at the container, the type sits in a
      column of its own beside it, and everything is set left so the eye has a
      single entry point. The bleed is what makes this feel like a held moment
      in the page rather than one more boxed section.
    */
    <section className="relative overflow-hidden bg-ink text-ivory">
      {/*
        Absolutely positioned from `lg` so it can escape the max-w-7xl
        container entirely — in normal flow, inside the container's own
        padding, it could never reach the viewport edge.
      */}
      <div className="relative h-64 w-full sm:h-80 lg:absolute lg:inset-y-0 lg:left-0 lg:h-full lg:w-[44%]">
        <BrandArtwork seed="brand-story" motif="ribbon" ratio="wide" className="h-full w-full" />

        {/*
          A gradient into the band's own colour, so the image resolves into
          the darkness instead of ending at a hard rectangle edge. This is the
          detail that separates a cinematic band from a picture placed beside
          some text — and it changes direction with the layout, fading upward
          while stacked and sideways once the image is alongside.
        */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-linear-to-t from-ink via-ink/40 to-transparent lg:bg-linear-to-r lg:from-transparent lg:via-ink/30 lg:to-ink"
        />
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`py-20 lg:ml-[50%] lg:max-w-xl lg:py-28 ${revealClasses(revealed)}`}
        >
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-accent/60" aria-hidden="true" />
            <p className="text-[0.6875rem] uppercase tracking-[0.3em] text-accent">Our story</p>
          </div>

          {/* Restrained scale, per the brief: the drama is the bleed and the
              inversion, not the point size. */}
          <h2 className="mt-7 font-display text-[2rem] leading-[1.12] tracking-[-0.01em] text-ivory sm:text-[2.75rem]">
            Glanz Luxe exists to make
            <span className="block italic text-accent">gifting more meaningful</span>
          </h2>

          <p className="mt-8 max-w-md text-[0.9375rem] leading-[1.9] text-ivory/70">
            We believe a gift is never just an object. It is a way of saying you were thought of,
            that a moment mattered. Every box we prepare is an attempt to carry that feeling —
            from the pieces we select to the ribbon that closes it.
          </p>

          {/* Hung off a rule rather than centred under the paragraph, so the
              tagline reads as a pulled quote instead of a caption. */}
          <p className="mt-10 border-l border-accent/40 pl-6 font-display text-xl italic text-accent sm:text-2xl">
            {tagline}
          </p>

          <div className="mt-10">
            <Link
              to="/about"
              className="group inline-flex items-center gap-3 border-b border-accent pb-1.5 text-sm tracking-wide text-ivory transition-colors hover:text-accent"
            >
              Read our story
              <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">
                →
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------ */

/** Wraps a section in the standard scroll reveal (§47). */
function Reveal({ children }: { children: React.ReactNode }) {
  const { ref, revealed } = useReveal<HTMLDivElement>();

  return (
    <div ref={ref} className={revealClasses(revealed)}>
      {children}
    </div>
  );
}
